import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, UserCheck, Clock, RefreshCw, Search, RotateCcw, Loader2, Download, Lock, KeyRound, ScanLine } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { formatDisplayDateTime } from '../../lib/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const REFRESH_INTERVAL = 15000; // 15s auto-refresh

const PSCheckinDashboard = () => {
  const [stats, setStats] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetting, setResetting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pinStatus, setPinStatus] = useState({ configured: false, lastRotatedAt: null });
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinSaving, setPinSaving] = useState(false);

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };
  const LIMIT = 30;

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ps/admin/checkin/stats`, { headers });
      setStats(res.data);
    } catch { /* silent */ }
  }, []);

  const fetchCheckins = useCallback(async () => {
    try {
      const params = { limit: LIMIT, skip: page * LIMIT };
      if (searchTerm) params.search = searchTerm;
      if (typeFilter) params.person_type = typeFilter;
      const res = await axios.get(`${API_URL}/api/ps/admin/checkin/list`, { headers, params });
      setCheckins(res.data.checkins);
      setTotal(res.data.total);
    } catch { /* silent */ }
  }, [page, searchTerm, typeFilter]);

  const fetchPinStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ps/admin/gate-staff-pin/status`, { headers });
      setPinStatus(res.data);
    } catch { /* silent */ }
  }, []);

  const handleSetPin = async () => {
    if (!/^\d{4,8}$/.test(pinInput)) {
      toast.error('PIN must be 4–8 digits');
      return;
    }
    setPinSaving(true);
    try {
      await axios.put(`${API_URL}/api/ps/admin/gate-staff-pin`, { pin: pinInput }, { headers });
      toast.success('Gate Staff PIN updated');
      setShowPinModal(false);
      setPinInput('');
      fetchPinStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update PIN');
    } finally {
      setPinSaving(false);
    }
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchStats(), fetchCheckins(), fetchPinStatus()]);
    setLastRefresh(new Date());
    setLoading(false);
  }, [fetchStats, fetchCheckins]);

  // Initial load + auto-refresh
  useEffect(() => { refresh(); }, [searchTerm, typeFilter, page]);
  useEffect(() => {
    const interval = setInterval(refresh, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [refresh]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    setSearchTerm(searchInput);
  };

  const handleReset = async () => {
    if (resetConfirm !== 'RESET') {
      toast.error('Please type RESET to confirm');
      return;
    }
    setResetting(true);
    try {
      const res = await axios.post(`${API_URL}/api/ps/admin/checkin/reset`, {}, { headers });
      toast.success(res.data.message || 'Check-ins reset');
      setShowResetModal(false);
      setResetConfirm('');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/api/ps/admin/checkin/export-excel`, {
        headers,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
      a.download = `ps-checkin-report-${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Check-in report downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const pct = (n, d) => d > 0 ? Math.round((n / d) * 100) : 0;
  const formatTime = (iso) => {
    if (!iso) return '-';
    try { return formatDisplayDateTime(iso); }
    catch { return iso; }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="checkin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="checkin-dashboard-title">Live Attendance</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Parichay Sangam 2026 — Event Check-in Dashboard
            {lastRefresh && (
              <span className="ml-2 text-xs text-gray-400">
                Last updated: {formatTime(lastRefresh.toISOString())}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading} data-testid="checkin-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <a href="/parichay-sangam/scan" target="_blank" rel="noopener noreferrer">
            <Button
              variant="outline"
              size="sm"
              className="text-orange-700 border-orange-300 hover:bg-orange-50"
              data-testid="scan-qr-btn"
            >
              <ScanLine className="w-4 h-4 mr-1.5" />
              Scan QR
            </Button>
          </a>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting}
            className="text-green-700 border-green-300 hover:bg-green-50"
            data-testid="checkin-export-excel-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-1.5" />
            )}
            Export to Excel
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setShowPinModal(true); setPinInput(''); }}
            className="text-slate-700 border-slate-300 hover:bg-slate-50"
            data-testid="gate-staff-pin-btn"
          >
            {pinStatus.configured ? <KeyRound className="w-4 h-4 mr-1.5" /> : <Lock className="w-4 h-4 mr-1.5" />}
            {pinStatus.configured ? 'Rotate Gate PIN' : 'Set Gate PIN'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setShowResetModal(true); setResetConfirm(''); }}
            disabled={loading || resetting}
            className="text-red-600 border-red-300 hover:bg-red-50"
            data-testid="checkin-reset-btn"
          >
            <RotateCcw className="w-4 h-4 mr-1.5" />
            Reset Check-ins
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="checkin-stats">
          <StatCard
            title="Total Checked In"
            value={stats.totalCheckins}
            sub={`of ${stats.totalExpected} expected`}
            pct={pct(stats.totalCheckins, stats.totalExpected)}
            Icon={UserCheck}
            color="emerald"
            testId="stat-total"
          />
          <StatCard
            title="Candidates"
            value={stats.candidateCheckins}
            sub={`of ${stats.totalExpectedCandidates} expected`}
            pct={pct(stats.candidateCheckins, stats.totalExpectedCandidates)}
            Icon={Users}
            color="orange"
            testId="stat-candidates"
          />
          <StatCard
            title="Family"
            value={stats.attendeeCheckins}
            sub={`of ${stats.totalExpectedAttendees} expected`}
            pct={pct(stats.attendeeCheckins, stats.totalExpectedAttendees)}
            Icon={Users}
            color="blue"
            testId="stat-attendees"
          />
          <StatCard
            title="Members"
            value={stats.memberCheckins || 0}
            sub={`of ${stats.totalExpectedMembers || 0} expected`}
            pct={pct(stats.memberCheckins || 0, stats.totalExpectedMembers || 0)}
            Icon={Users}
            color="purple"
            testId="stat-members"
          />
          <StatCard
            title="Attendance Rate"
            value={`${pct(stats.totalCheckins, stats.totalExpected)}%`}
            sub={`${stats.totalExpected - stats.totalCheckins} remaining`}
            Icon={Clock}
            color="violet"
            testId="stat-rate"
          />
        </div>
      )}

      {/* Progress Bar */}
      {stats && stats.totalExpected > 0 && (
        <div data-testid="checkin-progress">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>{stats.totalCheckins} checked in</span>
            <span>{stats.totalExpected} total</span>
          </div>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-700"
              style={{ width: `${Math.min(pct(stats.totalCheckins, stats.totalExpected), 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Recent Check-ins (Live Feed) */}
      {stats?.recentCheckins?.length > 0 && (
        <Card className="p-4" data-testid="recent-checkins">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            Recent Check-ins
          </h3>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {stats.recentCheckins.slice(0, 8).map((c, i) => (
              <div key={i} className="flex-shrink-0 bg-gray-50 rounded-lg px-3 py-2 min-w-[140px] border border-gray-100">
                <p className="text-sm font-medium text-gray-800 truncate">{c.name}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    c.type === 'Candidate' ? 'bg-orange-500' :
                    c.type === 'Member' ? 'bg-purple-500' : 'bg-blue-500'
                  }`} />
                  <span className="text-[10px] text-gray-500">{c.code}</span>
                  <span className="text-[10px] text-gray-400 ml-auto">{formatTime(c.checkedInAt)}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Filters + Full Log */}
      <Card className="p-4" data-testid="checkin-log">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
          <h3 className="text-sm font-semibold text-gray-700">Check-in Log</h3>
          <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-md ml-auto">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search name, code..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="pl-9 h-9 text-sm"
                data-testid="checkin-search"
              />
            </div>
            <select
              value={typeFilter}
              onChange={e => { setTypeFilter(e.target.value); setPage(0); }}
              className="h-9 px-3 border border-gray-200 rounded-md text-sm bg-white"
              data-testid="checkin-type-filter"
            >
              <option value="">All Types</option>
              <option value="Candidate">Candidates</option>
              <option value="Attendee">Family</option>
              <option value="Member">Members</option>
            </select>
          </form>
        </div>

        {checkins.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm" data-testid="checkin-empty">
            {loading ? 'Loading...' : 'No check-ins yet'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="checkin-table">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                    <th className="pb-2 pr-4 font-medium">Name</th>
                    <th className="pb-2 pr-4 font-medium">Code</th>
                    <th className="pb-2 pr-4 font-medium">Type</th>
                    <th className="pb-2 pr-4 font-medium">Family</th>
                    <th className="pb-2 font-medium">Checked In At</th>
                  </tr>
                </thead>
                <tbody>
                  {checkins.map((c, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="py-2.5 pr-4 font-medium text-gray-800">{c.name}</td>
                      <td className="py-2.5 pr-4 text-gray-600 font-mono text-xs">{c.code}</td>
                      <td className="py-2.5 pr-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          c.type === 'Candidate' ? 'bg-orange-100 text-orange-700' :
                          c.type === 'Member' ? 'bg-purple-100 text-purple-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {c.type === 'Attendee' ? 'Family' : c.type}
                        </span>
                      </td>
                      <td className="py-2.5 pr-4 text-gray-600 font-mono text-xs">{c.familyCode}</td>
                      <td className="py-2.5 text-gray-500 text-xs">{formatTime(c.checkedInAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-500">
                  Showing {page * LIMIT + 1}-{Math.min((page + 1) * LIMIT, total)} of {total}
                </p>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}
                    data-testid="checkin-prev">Prev</Button>
                  <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
                    data-testid="checkin-next">Next</Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="checkin-reset-modal">
          <Card className="max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <RotateCcw className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Reset All Check-ins?</h3>
                <p className="text-xs text-gray-500">सभी चेक-इन रिकॉर्ड हटाएं</p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-800 font-medium mb-1">
                This will clear <strong>{stats?.totalCheckins || 0}</strong> check-in records.
              </p>
              <p className="text-xs text-red-700">
                Records will be archived (safe to recover). Recommended only on event-day morning for a fresh start.
              </p>
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type <span className="font-bold font-mono text-red-600">RESET</span> to confirm:
            </label>
            <Input
              value={resetConfirm}
              onChange={(e) => setResetConfirm(e.target.value)}
              placeholder="Type RESET"
              className="font-mono"
              data-testid="checkin-reset-input"
              autoFocus
            />

            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => { setShowResetModal(false); setResetConfirm(''); }} disabled={resetting}>
                Cancel
              </Button>
              <Button
                onClick={handleReset}
                disabled={resetting || resetConfirm !== 'RESET'}
                className="bg-red-600 hover:bg-red-700"
                data-testid="checkin-reset-confirm-btn"
              >
                {resetting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resetting...</> : 'Reset All Check-ins'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Gate Staff PIN Modal */}
      {showPinModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="gate-pin-modal">
          <Card className="max-w-md w-full p-6">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="w-5 h-5 text-slate-700" />
              <h3 className="text-lg font-bold">{pinStatus.configured ? 'Rotate Gate Staff PIN' : 'Set Gate Staff PIN'}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Share this PIN only with gate staff. After entering it on a device, they can scan QR codes and check guests in for 12 hours.
            </p>
            {pinStatus.configured && pinStatus.lastRotatedAt && (
              <p className="text-[11px] text-gray-400 mb-3">
                Last rotated: {formatDisplayDateTime(pinStatus.lastRotatedAt)}
              </p>
            )}
            <Input
              type="password"
              inputMode="numeric"
              maxLength={8}
              placeholder="4–8 digit PIN"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
              className="mb-3 text-center text-lg tracking-[0.4em] font-semibold"
              data-testid="gate-pin-input"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowPinModal(false)} disabled={pinSaving} data-testid="gate-pin-cancel">
                Cancel
              </Button>
              <Button
                onClick={handleSetPin}
                disabled={pinSaving || !/^\d{4,8}$/.test(pinInput)}
                className="bg-slate-800 hover:bg-slate-900"
                data-testid="gate-pin-save"
              >
                {pinSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : (pinStatus.configured ? 'Update PIN' : 'Set PIN')}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

function StatCard({ title, value, sub, pct, Icon, color, testId }) {
  const colorMap = {
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    orange: 'bg-orange-50 text-orange-600 border-orange-100',
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    violet: 'bg-violet-50 text-violet-600 border-violet-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
  };
  return (
    <Card className={`p-4 border ${colorMap[color]?.split(' ')[2] || 'border-gray-100'}`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-gray-500 font-medium">{title}</p>
          <p className="text-2xl font-bold mt-1 text-gray-900">{value}</p>
          {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <div className={`p-2 rounded-lg ${colorMap[color]?.split(' ').slice(0, 2).join(' ')}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      {pct !== undefined && typeof pct === 'number' && (
        <div className="mt-3">
          <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                color === 'emerald' ? 'bg-emerald-500' :
                color === 'orange' ? 'bg-orange-500' :
                color === 'blue' ? 'bg-blue-500' :
                color === 'purple' ? 'bg-purple-500' : 'bg-violet-500'
              }`}
              style={{ width: `${Math.min(pct, 100)}%` }}
            />
          </div>
        </div>
      )}
    </Card>
  );
}

export default PSCheckinDashboard;
